import {
    closeConversation,
    endConversation,
    transferConversation,
} from '../../../actions/modal'
import {
    useAppDispatch,
    useAppSelector,
} from 'hooks'
import {
    useChannelDetails, useQueues,
} from '../../../hooks/ninchat'
import {AudienceMenubarProps} from './types'
import AudienceMenubarView from './AudienceMenubarView'
import {NinchatMessages} from '../../../api/types'
import React from 'react'
import {blockAudienceAddress} from 'api/action/blockAudienceAddress'
import {getActiveQueueSettings} from 'utils/queueSettings'
import {isScheduledAppointment} from '../../../utils/channels'
import {jitsiToggleChat} from '../../../actions/jitsi'
import {navigation} from '../../../navigation'
import permissionGuard from 'components/Navigation/helpers/PermissionGuard'
import {sendMessage} from '../../../api/action/sendMessage'
import {toast} from 'components/Toast'
import {updateChannel} from 'api/action/updateChannel'
import {useTranslation} from 'react-i18next'

const AudienceMenubar = (props: AudienceMenubarProps): React.ReactElement => {
    const {
        chat,
        isActiveVideoChannel,
        isChannelSidebarOpen,
        isChatOpen,
        onToggleChannelSidebar,
    } = props

    const channelId = chat.channel_id
    const dispatch = useAppDispatch()
    const {t} = useTranslation()

    const audienceId = useAppSelector(({ninchat}): string => {
        if (!channelId) {
            return ''
        }
        return ninchat.user_channels[channelId]?.channel_attrs?.audience_id ?? ''
    })

    const hasChannelPermission = permissionGuard.hasManageRealmChannelPermissions(channelId || '')

    const isChannelClosed = useAppSelector(({ninchat}): boolean => {
        if (!channelId) {
            return true
        }
        return ninchat.user_channels[channelId]?.channel_attrs?.closed === true
    })

    const isChannelSuspended = useAppSelector(({ninchat}): boolean => {
        if (!channelId) {
            return true
        }
        return ninchat.user_channels[channelId]?.channel_attrs?.suspended === true
    })

    const isVideoChannel = useAppSelector(({ninchat}): boolean => {
        if (!channelId) {
            return false
        }
        return ninchat.user_channels[channelId]?.channel_attrs?.video === 'group'
    })

    const isScheduledAppointmentChannel = useAppSelector(({ninchat}): boolean => {
        if (!channelId) {
            return false
        }
        return isScheduledAppointment(ninchat.user_channels?.[channelId])
    })

    const queueId = useAppSelector(({ninchat}): string => {
        if (!channelId) {
            return ''
        }
        return ninchat.user_channels[channelId]?.channel_attrs?.queue_id ?? ''
    })

    const queue = useQueues([queueId], false)[queueId]
    const parentGroupQueueId = queue?.queue_attrs?.group_id
    const parentGroupQueue = useQueues(parentGroupQueueId ? [parentGroupQueueId] : [], false)[parentGroupQueueId ?? '']
    const queueSettings = getActiveQueueSettings({
        parentGroupQueue,
        queue,
    })

    const channelDetails = useChannelDetails({channelId})

    const hasUnreadMessages = !isChatOpen && ['unread', 'highlight'].includes(channelDetails?.channelStatus ?? '')

    const transferConversationModal = () => {
        dispatch(transferConversation({
            channelId,
            queueId,
            show: true,
        }))
    }

    const endConversationModal = () => {
        dispatch(endConversation({
            channelId,
            show: true,
        }))
    }

    const closeConversationModal = () => {
        dispatch(closeConversation({
            channelId,
            show: true,
        }))
    }

    const loadTranscript = () => {
        if (audienceId) {
            navigation.push('Transcript', {audience_id: audienceId})
        }
    }

    const onBlockIpAddress = (): void => {
        dispatch(blockAudienceAddress({audience_id: audienceId}))
            .unwrap()
            .then(() => {
                toast.success(t('IP address blocked'))
            }, () => {
                toast.error(t('Error blocking IP address'))
            })
    }

    const onRequestAuthentication = (delegation?: boolean): void => {
        const data = {
            sso: {
                Authentication: 'requested',
                ...delegation && {Delegation: 'requested'},
            },
        }

        dispatch(sendMessage({
            ...chat,
            message_fold: true,
            message_type: NinchatMessages.NinchatMessageTypes.NinchatMetadataMessage,
            payload: [JSON.stringify({data})],
        }))
    }

    const onToggleChat = (): void => {
        if (isChatOpen && isChannelSidebarOpen) {
            onToggleChannelSidebar()
        } else if (!isChatOpen && isChannelSidebarOpen) {
            onToggleChannelSidebar()
            dispatch(jitsiToggleChat())
        } else {
            dispatch(jitsiToggleChat())
        }
    }

    const onToggleVideo = () => {
        if (channelId) {
            dispatch(updateChannel({
                channel_attrs: {video: isVideoChannel ? null : 'group'},
                channel_id: channelId,
            }))
        }
    }

    return (
        <AudienceMenubarView
            audienceRegistration={queueSettings?.audience_registration ?? false}
            chat={chat}
            closeConversationModal={closeConversationModal}
            endConversationModal={endConversationModal}
            hasChannelPermission={hasChannelPermission}
            hasUnreadMessages={hasUnreadMessages}
            isActiveVideoChannel={isActiveVideoChannel}
            isChannelClosed={isChannelClosed}
            isChannelSidebarOpen={isChannelSidebarOpen}
            isChannelSuspended={isChannelSuspended}
            isScheduledAppointmentChannel={isScheduledAppointmentChannel}
            isVideoChannel={isVideoChannel}
            loadTranscript={loadTranscript}
            onBlockIpAddress={onBlockIpAddress}
            onRequestAuthentication={onRequestAuthentication}
            onToggleChannelSidebar={onToggleChannelSidebar}
            onToggleChat={onToggleChat}
            onToggleVideo={onToggleVideo}
            queueSso={queue?.queue_sso}
            transferConversationModal={transferConversationModal}
        />
    )
}

export default AudienceMenubar
